import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { DeviceTimeSlotsResponse, DimensionRow } from "../api/client";

const formatter = new Intl.NumberFormat("zh-HK");
const percentageFormatter = new Intl.NumberFormat("zh-HK", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const DEVICE_COLORS = ["#1c2676", "#d97706", "#2563eb", "#7c3aed", "#be123c", "#64748b"];
const WEEKDAY_ORDER = [
  { value: 1, label: "週一" },
  { value: 2, label: "週二" },
  { value: 3, label: "週三" },
  { value: 4, label: "週四" },
  { value: 5, label: "週五" },
  { value: 6, label: "週六" },
  { value: 0, label: "週日" }
];
const TIME_BUCKETS = [
  { label: "00-03", start: 0, end: 3 },
  { label: "04-07", start: 4, end: 7 },
  { label: "08-11", start: 8, end: 11 },
  { label: "12-15", start: 12, end: 15 },
  { label: "16-19", start: 16, end: 19 },
  { label: "20-23", start: 20, end: 23 }
];

function formatPercentage(value: number): string {
  return `${percentageFormatter.format(value)}%`;
}

function buildHourlyRows(timeSlots: DeviceTimeSlotsResponse | null) {
  const deviceTypes = Array.from(new Set(timeSlots?.hourly.results.map((row) => row.device_type) ?? [])).sort();
  const rows = Array.from({ length: 24 }, (_, hour) => {
    const row: Record<string, string | number> = { hour, hourLabel: `${String(hour).padStart(2, "0")}:00` };
    for (const device of deviceTypes) row[device] = 0;
    return row;
  });

  for (const item of timeSlots?.hourly.results ?? []) {
    rows[item.hour][item.device_type] = item.views;
  }

  return { rows, deviceTypes };
}

function buildWeeklyRows(timeSlots: DeviceTimeSlotsResponse | null) {
  const weeklyRows = WEEKDAY_ORDER.map((weekday) => ({
    weekday,
    buckets: TIME_BUCKETS.map((bucket) => {
      const views = (timeSlots?.weekly.results ?? [])
        .filter((row) => row.weekday === weekday.value && row.hour >= bucket.start && row.hour <= bucket.end)
        .reduce((sum, row) => sum + row.views, 0);
      return { ...bucket, views };
    })
  }));
  const maxViews = Math.max(1, ...weeklyRows.flatMap((row) => row.buckets.map((bucket) => bucket.views)));
  return { weeklyRows, maxViews };
}

export function DeviceAnalysisPage({
  rows,
  timeSlots
}: {
  rows: DimensionRow[];
  timeSlots: DeviceTimeSlotsResponse | null;
}) {
  const { rows: hourlyRows, deviceTypes } = buildHourlyRows(timeSlots);
  const { weeklyRows, maxViews } = buildWeeklyRows(timeSlots);

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <h2>裝置分析</h2>
      </div>
      <section className="panel">
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={rows.slice(0, 12)} layout="vertical" margin={{ left: 32 }}>
            <CartesianGrid stroke="#e5e7eb" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={120} />
            <Tooltip />
            <Bar dataKey="views" name="觀看" fill="#1c2676" />
          </BarChart>
        </ResponsiveContainer>
      </section>
      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>項目</th>
                <th>觀看</th>
                <th>佔比</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{formatter.format(row.views)}</td>
                  <td>{formatPercentage(row.percentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h3>每日時段分析</h3>
            <span>0-23 點觀看數，依裝置堆疊</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={hourlyRows}>
            <CartesianGrid stroke="#e5e7eb" />
            <XAxis dataKey="hourLabel" tickMargin={8} interval={1} />
            <YAxis width={54} />
            <Tooltip />
            {deviceTypes.map((device, index) => (
              <Bar key={device} dataKey={device} name={device} stackId="device" fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h3>每週時段分析</h3>
            <span>星期與四小時區間觀看熱度</span>
          </div>
        </div>
        <div className="table-wrap">
          <table className="time-heatmap-table">
            <thead>
              <tr>
                <th>星期</th>
                {TIME_BUCKETS.map((bucket) => (
                  <th key={bucket.label}>{bucket.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeklyRows.map((row) => (
                <tr key={row.weekday.value}>
                  <th>{row.weekday.label}</th>
                  {row.buckets.map((bucket) => {
                    const heat = bucket.views / maxViews;
                    return (
                      <td key={bucket.label}>
                        <span className="heatmap-cell" style={{ backgroundColor: `rgba(28, 38, 118, ${0.08 + heat * 0.62})` }}>
                          {formatter.format(bucket.views)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
